import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import AmbassadorsYearDropdown from '../../AmbassadorsYearDropdown/index'
import Table from '../../../Table/index'
import Link from '../../../Link/index'
import { VALUED_CELL_MONEY_TYPE, VALUED_CELL_PERCENTAGE_TYPE, ValuedCell } from '../../../Table/TableCell/index'
import {
  AMBASSADORS_PAGE_STATIC_KEY,
  CAMPAIGN_FIELD_BILLINGS,
  CAMPAIGN_FIELD_CLICKS,
  CAMPAIGN_FIELD_CLICK_TO_LEAD,
  CAMPAIGN_FIELD_EARNINGS,
  CAMPAIGN_FIELD_EARNINGS_PER_CLICK,
  CAMPAIGN_FIELD_EARNINGS_PER_LEADS,
  CAMPAIGN_FIELD_LEADS,
  CAMPAIGN_FIELD_LEAD_TO_SIGNUP,
  CAMPAIGN_FIELD_NAME,
  CAMPAIGN_FIELD_SIGNUPS,
  createCsvTitle,
  getCampaignsCsvData,
  getCsvHeaders,
  getPastUpcomingCampaigns,
  getSubHeaderCampaignsMetricsTableData,
  nextYear,
} from '../../../../services/ambassadors/index'
import { getAmbassadorCampaigns } from '../../../../services/ambassadors/actions'
import {
  selectAmbassadorCampaigns,
  selectAmbassadorCampaignsError,
  selectAmbassadorCampaignsProcessing,
  selectAmbassadorDetailsData,
  selectAmbassadorUpcomingCampaigns,
  selectAmbassadorUpcomingCampaignsProcessing,
} from '../../../../services/ambassadors/selectors'
import { selectStaticTextByKey } from '../../../../services/static-text/selectors'
import { selectLocale } from '../../../../services/page/index'
import { exportCSVFile } from '../../../../services/file/index'
import { H2, HEADING_TYPES } from '../../../Heading/index'
import Loading, { LOADING_ICON_BLUE_DARK_BG_WHITE } from '../../../Loading/index'

const getColumns = () => {
  const staticText = useSelector(selectStaticTextByKey(AMBASSADORS_PAGE_STATIC_KEY))

  return [
    {
      key: CAMPAIGN_FIELD_NAME,
      title: staticText.get('campaignNameColumn', ''),
      sorter: true,
      render: (value, item) => {
        const utmCampaign = item.get('utmCampaign')

        if (!utmCampaign) {
          return value
        }

        return (
          <Link to={`ambassadors?tab=partner-campaigns&utmCampaign=${utmCampaign}`}>
            <b>{value}</b>
          </Link>
        )
      },
    },
    {
      key: CAMPAIGN_FIELD_CLICKS,
      title: staticText.get('clicksColumn', ''),
      sorter: true,
      tooltipTitle: staticText.get('clicksTooltip'),
    },
    {
      key: CAMPAIGN_FIELD_LEADS,
      title: staticText.get('leadsColumn', ''),
      sorter: true,
      tooltipTitle: staticText.get('leadsTooltip'),
    },
    {
      key: CAMPAIGN_FIELD_SIGNUPS,
      title: staticText.get('signupsColumn', ''),
      sorter: true,
      tooltipTitle: staticText.get('signupsTooltip'),
    },
    {
      key: CAMPAIGN_FIELD_BILLINGS,
      title: staticText.get('paymentsColumn', ''),
      sorter: true,
      tooltipTitle: staticText.get('billingsTooltip'),
    },
    {
      key: CAMPAIGN_FIELD_EARNINGS,
      title: staticText.get('earningsColumn', ''),
      sorter: true,
      tooltipTitle: staticText.get('earningsTooltip'),
      render: (value) => <ValuedCell value={value} type={VALUED_CELL_MONEY_TYPE} />,
    },
    {
      key: CAMPAIGN_FIELD_EARNINGS_PER_CLICK,
      title: staticText.get('earningsPerClickColumn', ''),
      sorter: true,
      tooltipTitle: staticText.get('earningsPerClickTooltip'),
      render: (value) => <ValuedCell value={value} type={VALUED_CELL_MONEY_TYPE} />,
    },
    {
      key: CAMPAIGN_FIELD_EARNINGS_PER_LEADS,
      title: staticText.get('earningsPerLeadColumn', ''),
      sorter: true,
      tooltipTitle: staticText.get('earningsPerLeadsTooltip'),
      render: (value) => <ValuedCell value={value} type={VALUED_CELL_MONEY_TYPE} />,
    },
    {
      key: CAMPAIGN_FIELD_CLICK_TO_LEAD,
      title: staticText.get('leadConversionRateColumn', ''),
      sorter: true,
      tooltipTitle: staticText.get('clickToLeadTooltip'),
      render: (value) => <ValuedCell value={value} type={VALUED_CELL_PERCENTAGE_TYPE} />,
    },
    {
      key: CAMPAIGN_FIELD_LEAD_TO_SIGNUP,
      title: staticText.get('signupConversionRateColumn', ''),
      sorter: true,
      tooltipTitle: staticText.get('leadToSignupTooltip'),
      render: (value) => <ValuedCell value={value} type={VALUED_CELL_PERCENTAGE_TYPE} />,
    },
  ]
}

/**
 * Upcoming Campaigns section
 * @returns {JSX.Element}
 */
const UpcomingCampaigns = () => {
  const staticText = useSelector(selectStaticTextByKey(AMBASSADORS_PAGE_STATIC_KEY))
  const upcomingCampaigns = useSelector(selectAmbassadorUpcomingCampaigns)
  const isUpcomingCampaignsProcessing = useSelector(selectAmbassadorUpcomingCampaignsProcessing)

  if (isUpcomingCampaignsProcessing) {
    <Loading visible icon={LOADING_ICON_BLUE_DARK_BG_WHITE} />
  }

  if (!upcomingCampaigns || !upcomingCampaigns.size) {
    return <></>
  }

  return (
    <div className="table-container">
      <div>
        <H2 as={HEADING_TYPES.H4}>{staticText.get('upcomingCampaigns', '')}</H2>
      </div>
      <table className="table">
        <tbody>
          {upcomingCampaigns.map((upcomingCampaign) => (
            <tr key={upcomingCampaign.get('campaignUuid')} className="table-row">
              <td className="table-cell">
                <Link to={`ambassadors?tab=partner-campaigns&utmCampaign=${upcomingCampaign.get('utmCampaign')}`}>
                  <b>{upcomingCampaign.get('campaignName', '')}</b>
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const PartnerCampaignsTab = ({ history }) => {
  const staticText = useSelector(selectStaticTextByKey(AMBASSADORS_PAGE_STATIC_KEY))
  const isCampaignsProcessing = useSelector(selectAmbassadorCampaignsProcessing)
  const campaignsError = useSelector(selectAmbassadorCampaignsError)
  const campaigns = useSelector(selectAmbassadorCampaigns)
  const ambassadorDetailsData = useSelector(selectAmbassadorDetailsData)
  const locale = useSelector(selectLocale)
  const dispatch = useDispatch()

  const date = new Date()
  const year = date.getFullYear()
  const [selectedYear, setSelectedYear] = useState(year)

  const isNextYear = Number(selectedYear) === Number(nextYear)

  const [pastCampaigns] = getPastUpcomingCampaigns(campaigns)

  const columns = getColumns()

  const subHeaderData = getSubHeaderCampaignsMetricsTableData(campaigns, {
    [CAMPAIGN_FIELD_NAME]: `${selectedYear} ${staticText.get('total')}`,
  })

  const handleChangeYear = (selectedY) => {
    dispatch(getAmbassadorCampaigns(selectedY))
    setSelectedYear(selectedY)
  }

  const handleExport = () => {
    const headers = getCsvHeaders(columns)
    const csvData = getCampaignsCsvData(campaigns).toJS()

    const csvTitle = createCsvTitle({
      prefix: staticText.get('csvPartnerPrefix'),
      type: staticText.get('csvCampaignsTitle'),
      locale,
      downloaded: staticText.get('Downloaded'),
      name: ambassadorDetailsData.get('name', ''),
      year: selectedYear,
    })

    exportCSVFile(headers, csvData, csvTitle)
  }

  if (campaignsError) {
    return (
      <div>
        <div className="ambassadors-page__error">
          {staticText.get('somethingWentWrong')}
        </div>
        <UpcomingCampaigns />
      </div>
    )
  }

  return (
    <div>
      <div className="ambassadors-page__selector-container">
        <AmbassadorsYearDropdown
          onSubmit={handleExport}
          onChange={handleChangeYear}
          withNextYear
        />
      </div>
      {!isNextYear && (
        <>
          <div className="ambassadors-page__flexible-table-container">
            <Table
              columns={columns}
              subHeaderData={subHeaderData}
              tableData={pastCampaigns}
              history={history}
              historyCallback={() => dispatch(getAmbassadorCampaigns(selectedYear))}
              rowKey="utmCampaign"
              loading={isCampaignsProcessing}
            />
          </div>
          <div className="table-legend">
            {staticText.get('calculation', '')}
          </div>
          <div className="table-legend">
            {staticText.get('insufficient', '')}
          </div>
        </>
      )}
      <UpcomingCampaigns />
    </div>
  )
}

export default PartnerCampaignsTab
